import { z } from 'zod';
import { userSchema } from './user';

export const reportStatuses = ['Pending', 'Created', 'Failed'] as const;

export const reportSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  type: z.string(),
  s3Path: z.string(),
  userID: z.string().nullable(),
  cognitoID: z.string().nullable(),
  siteID: z.string(),
  status: z.enum(reportStatuses).default('Pending'),
  userEmail: z.string().optional(),
  providerID: z.guid().nullable().optional(),
  tariffID: z.string().optional(),
  patientID: z.string().optional(),
  submissionID: z.string().optional(),
  remittanceID: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  payorID: z.string().optional(),
  user: z.lazy(() => userSchema.optional()),
  headers: z.array(z.string()).optional(),
});

export const reportExportTypeSchema = z.enum(['pdf', 'csv']);

export const customReportPayloadSchema = z
  .object({
    siteID: z.guid('siteID must be a UUID'),
    userID: z.guid('userID must be a UUID'),
    cognitoID: z.guid('cognitoID must be a UUID'),
    providerID: z.guid('providerID must be a UUID').nullable(),
    includePayorSummary: z.boolean().default(false),
    includeProviderSummary: z.boolean().default(false),
    exportType: reportExportTypeSchema.default('pdf'),
    dataType: z.enum(['serviceDate', 'paymentDate']),
    startDate: z.iso.date(),
    endDate: z.iso.date(),
    payorIDs: z.array(z.guid('payorID must be a UUID')),
    groupIDs: z.array(z.guid('groupID must be a UUID')).optional(),
    hospitalCodes: z.array(z.string()),
    eobIDs: z.array(z.guid('eobID must be a UUID')),
    tariffCode: z.string().nullable(),
    diagnosisCode: z.string().nullable(),
    headers: z.array(z.string()),
  })
  .strict();

export const accountsReceivableReportPayloadSchema = z
  .object({
    siteID: z.string(),
    userID: z.string(),
    cognitoID: z.string(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    headers: z.array(z.string()),
    providerID: z.string().nullable().optional(),
    exportType: reportExportTypeSchema,
  })
  .strict();

export const groupIncomeReportPayloadSchema = z
  .object({
    siteID: z.string(),
    userID: z.string(),
    groupName: z.string().optional(),
    remittanceID: z.string().optional(),
    exportType: reportExportTypeSchema,
  })
  .strict();

export const statementIncomeReportPayloadSchema = z
  .object({
    siteID: z.string(),
    cognitoID: z.string(),
    providerID: z.string().nullable().optional(),
    remittanceID: z.string().optional(),
  })
  .strict();

export const tariffProviderReportPayloadSchema = z
  .object({
    siteID: z.string(),
    userID: z.string(),
    cognitoID: z.string(),
    providerID: z.string().nullable().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    headers: z.array(z.string()),
    tariffIDs: z.array(z.string()).optional(),
  })
  .strict();

export const providerReportPayloadSchema = z
  .object({
    siteID: z.string(),
    userID: z.string(),
    providerID: z.string().nullable().optional(),
    subtype: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    remittanceID: z.string().optional(),
    dateType: z.enum(['serviceDate', 'paymentDate']).optional(),
    groupIDs: z.array(z.string()).optional(),
    reportScope: z.enum(['provider', 'site']).optional(),
    exportType: reportExportTypeSchema,
    timeZone: z.string(),
  })
  .strict()
  .superRefine((payload, ctx) => {
    const isSiteMonthlyRevenueStatement =
      payload.subtype === 'monthlyRevenueStatement' &&
      payload.reportScope === 'site';

    if (!isSiteMonthlyRevenueStatement && !payload.providerID) {
      ctx.addIssue({
        code: 'custom',
        path: ['providerID'],
        message: 'providerID is required for provider-scoped reports',
      });
    }

    if (
      payload.reportScope === 'site' &&
      payload.subtype !== 'monthlyRevenueStatement'
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['reportScope'],
        message: 'Site scope is only available for monthly revenue statements',
      });
    }
  });

export const reportGenerationRequestSchema = z.discriminatedUnion('type', [
  z
    .object({
      type: z.literal('accountsReceivable'),
      payload: accountsReceivableReportPayloadSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('groupIncome'),
      payload: groupIncomeReportPayloadSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('statementIncome'),
      payload: statementIncomeReportPayloadSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('tariffProviderReport'),
      payload: tariffProviderReportPayloadSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('providerReport'),
      payload: providerReportPayloadSchema,
    })
    .strict(),
  z
    .object({
      type: z.literal('customReport'),
      payload: customReportPayloadSchema,
    })
    .strict(),
]);

export type ReportGenerationRequest = z.infer<
  typeof reportGenerationRequestSchema
>;
